/**
 * Chunk U6 — Upload queue polish helpers (quiet UX, CN teach-once, type check).
 */

import {
  UPLOAD_QUEUE_COPY,
  normalizeUsagePlanName,
  resolvePlanMonthlyUploadCap,
} from './invoiceUploadQueueHelpers'
import { formatInvoiceUploadsHabitProgress } from './growthEngineHabitProgress'
import { isPaidGrowthEnginePlan } from './growthEngineHumanPath'

/** OCR guesses that must stay tagged Invoice with an amber “Check type” nudge. */
export const OCR_GUESS_STAY_INVOICE = new Set([
  'delivery_note',
  'statement',
  'unknown',
  'other',
  'receipt',
])

/**
 * Map OCR document-type guess → upload select value.
 * Credit notes map through; delivery notes / statements stay Invoice.
 */
export function mapOcrGuessToUploadDocumentType(documentTypeGuess) {
  const guess = String(documentTypeGuess || '')
    .trim()
    .toLowerCase()
  if (guess === 'credit_note' || guess === 'creditnote') {
    return 'creditNote'
  }
  return 'invoice'
}

/**
 * Whether the focus card should show amber “Check type” guidance.
 */
export function shouldShowDocumentTypeCheckAmber(file = {}) {
  const guess = String(
    file?.ocrSuggested?.documentTypeGuess || file?.detectedDocumentType || '',
  )
    .trim()
    .toLowerCase()
  const warnings = Array.isArray(file?.ocrWarnings) ? file.ocrWarnings : []
  if (warnings.includes('looks_like_delivery_note')) return true
  if (warnings.includes('looks_like_statement')) return true
  if (!guess) return false
  if (
    guess === 'invoice' ||
    guess === 'credit_note' ||
    guess === 'creditnote'
  ) {
    return false
  }
  return OCR_GUESS_STAY_INVOICE.has(guess) || guess !== 'invoice'
}

export function getDocumentTypeCheckAmberCopy(file = {}) {
  if (!shouldShowDocumentTypeCheckAmber(file)) return ''
  return UPLOAD_QUEUE_COPY.checkDocumentType
}

/** Quiet progress: only show while OCR is actively reading / queued. */
export function shouldShowQuietUploadProgress({
  reading = 0,
  queued = 0,
  ocrActive = false,
} = {}) {
  if (ocrActive) return true
  return Number(reading) > 0 || Number(queued) > 0
}

export function getQuietUploadProgressLine({
  progressLine = '',
  reading = 0,
  queued = 0,
  ocrActive = false,
} = {}) {
  if (!shouldShowQuietUploadProgress({ reading, queued, ocrActive })) {
    return ''
  }
  return String(progressLine || '').trim()
}

/** CN teaching — once per venue until dismissed. */
export function getCnQueueTeachStorageKey(venueId) {
  return `invoices_cn_queue_teach_dismissed_${String(venueId || '').trim()}`
}

export function isCnQueueTeachDismissed(venueId) {
  if (!venueId || typeof localStorage === 'undefined') return false
  return localStorage.getItem(getCnQueueTeachStorageKey(venueId)) === '1'
}

export function dismissCnQueueTeach(venueId) {
  if (!venueId || typeof localStorage === 'undefined') return
  localStorage.setItem(getCnQueueTeachStorageKey(venueId), '1')
}

export function shouldShowCnQueueTeach(venueId, { isCreditNote = false } = {}) {
  if (!isCreditNote || !venueId) return false
  return !isCnQueueTeachDismissed(venueId)
}

/**
 * Plan-limit copy aligned with Lite (internal Free) / Starter / Growth / Pro allowances.
 * (Dashboard caps: Free 5 · Starter 25 · Growth 100 · Pro unlimited)
 */
export const UPLOAD_PLAN_LIMIT_COPY = {
  freeMonthly: 5,
  starterMonthly: 25,
  growthMonthly: 100,
  planSummary: 'Lite 5/mo · Starter 25/mo · Growth 100/mo · Pro unlimited',
  exhaustedBody:
    'You’ve used this month’s invoice uploads. OMS Lite includes 5/month, Starter 25, Growth 100, and Pro is unlimited — upgrade your plan, or wait until next month.',
  remainingBody: remaining =>
    `You can upload ${remaining} more this month (${UPLOAD_PLAN_LIMIT_COPY.planSummary}). Select that many or fewer, or upgrade.`,
  usageHeading: 'Invoice uploads this month',
  limitReachedTitle: 'Monthly invoice upload limit reached',
  upgradeCta: 'Upgrade plan',
  needMoreTitle: 'Need more invoice uploads?',
  needMoreBody:
    'Upgrade your plan for higher limits and unlock more invoice uploads, faster three-way matching, and additional procurement features.',
}

/** Monthly invoice upload cap for a plan (`null` = Pro / unlimited). */
export function resolveInvoiceUploadMonthlyCap(currentPlan) {
  return resolvePlanMonthlyUploadCap(currentPlan)
}

/**
 * Dashboard usage chip / limit banner summary.
 * `remainingQuota` from resolveUploadRemainingQuota (`null` = unlimited).
 */
export function getInvoiceUploadUsageSummary({
  currentPlan,
  remainingQuota,
} = {}) {
  const plan = normalizeUsagePlanName(currentPlan)
  const monthlyLimit = resolveInvoiceUploadMonthlyCap(plan)
  if (monthlyLimit == null || remainingQuota == null) {
    return {
      isUnlimited: true,
      isAtLimit: false,
      monthlyLimit: null,
      remaining: null,
      used: null,
      usageLine: null,
      limitReachedBody: null,
    }
  }

  const remaining = Math.max(0, Number(remainingQuota) || 0)
  const used = Math.min(monthlyLimit, Math.max(0, monthlyLimit - remaining))
  const isAtLimit = remaining <= 0
  const paid = isPaidGrowthEnginePlan(plan)
  const usageLine = formatInvoiceUploadsHabitProgress(used, monthlyLimit)
  const limitReachedBody = paid
    ? `You've uploaded ${used} of ${monthlyLimit} invoices this month.`
    : `You've uploaded ${used} of ${monthlyLimit} invoices this month. Upgrade your plan to continue uploading invoices automatically.`

  return {
    isUnlimited: false,
    isAtLimit,
    monthlyLimit,
    remaining,
    used,
    usageLine,
    limitReachedBody,
  }
}

/** Compact review chrome when the batch is a single file (GR / one-off). */
export function shouldUseCompactUploadReviewChrome(draftCount = 0) {
  return Number(draftCount) <= 1
}
